import type { ExperimentConfig } from '@/types';

const experiment4Config: ExperimentConfig = {
  id: 'exp4',
  number: 4,
  name: 'EDTA Standardization',
  description: 'Prepare an EDTA solution from Na₂H₂Y·2H₂O, standardize it against 0.01 M Mg²⁺ standard (MgCl₂) with Eriochrome Black T indicator, and determine calcium concentration in an unknown water sample. No formal report required.',
  estimatedTime: '150 minutes',
  learningObjectives: [
    'Prepare a ~0.01 M EDTA solution from Na₂H₂Y·2H₂O (disodium salt dihydrate) with one pellet of NaOH in 500 mL',
    'Understand EDTA as a hexadentate ligand with six potential bonding sites (pK₁=2.0, pK₂=2.67, pK₃=6.16, pK₄=10.26)',
    'Standardize EDTA against 0.01 M Mg²⁺ standard (MgCl₂) using 3 × 25 mL aliquots',
    'Recognize the EBT indicator endpoint: colour change from red to distinct blue',
    'Perform a blank correction (25 mL deionised water + 1 mL buffer + 2-4 drops EBT)',
    'Calculate EDTA molarity: M(EDTA) = (Volume Mg²⁺ std (L) × Molarity Mg²⁺ std) / Titre EDTA (L)',
    'Determine calcium in unknown water sample (diluted 5-fold) with 2 mL buffer, 1 mL Mg/EDTA, 5-6 drops EBT',
    'Calculate ppm Ca²⁺ = (moles Ca²⁺ × Molar Mass Ca²⁺ × 10⁶) / 25 g',
  ],
  safetyNotes: [
    'Wear safety glasses at all times while in the laboratory',
    'Use a rubber safety bulb when transferring solutions with a pipet - do not pipet by mouth',
    'Sulfuric acid is dangerous to the skin - wash immediately if spilled and inform the instructor',
    'Ammonia is a respiratory irritant and heart stimulant - confine pouring of ammonia buffer to exhaust hood',
  ],
  requiredEquipment: [
    'Buret',
    '25 mL pipet',
    '250 mL Erlenmeyer flasks (3 for Part I, 3 for Part II)',
    '500 mL volumetric flask',
    '500 mL beaker',
    'Glass stir rod or magnetic stir bar',
    'Analytical balance',
    'Weighing boat',
    'Na₂H₂Y·2H₂O (dried EDTA disodium salt)',
    'One pellet of NaOH',
    '0.01 M Mg²⁺ standard solution (MgCl₂)',
    'Eriochrome Black T indicator solution (or Calmagite)',
    'Ammonia/Ammonium Chloride Buffer, pH 10',
    'Mg/EDTA solution (for sharper colour change)',
    'Unknown water sample',
    'Rubber safety bulb',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'preparation',
      title: 'Part I: EDTA Preparation',
      equipment: ['Analytical balance', 'Weighing boat', '500 mL beaker', '500 mL volumetric flask', 'Glass stir rod'],
      initialState: {
        edtaMass: 0,
        isDissolving: false,
        isDiluted: false,
        solutionPrepared: false
      },
      steps: [
        {
          id: 1,
          title: 'Weigh EDTA',
          shortDescription: 'Accurately weigh around 1.8000 to 1.9000 g dried EDTA in weighing boat',
          handler: 'handleWeighEDTA',
          infoContent: {
            title: 'What is EDTA?',
            content: `EDTA (ethylenediaminetetraacetic acid) is a hexadentate ligand — it has six potential sites for bonding with a metal ion (four acidic hydrogens plus unshared electron pairs on each nitrogen).

We use the **disodium salt Na₂H₂Y·2H₂O** which is most useful for analytical purposes. It is obtainable in high purity as the dihydrate. Solutions are stable for months in plastic or borosilicate glass.

EDTA pK values: pK₁ = 2.0, pK₂ = 2.67, pK₃ = 6.16, pK₄ = 10.26.

Accurately weigh around **1.8000 to 1.9000 g** of dried EDTA in a weighing boat, record the weight.`,
            whatHappens: 'Dried EDTA disodium salt (Na2H2Y.2H2O) is accurately weighed on an analytical balance to prepare a solution of approximately 0.01 M concentration.',
            observe: [
              'Tare the weighing boat before adding EDTA',
              'Record the mass to all four decimal places (e.g., 1.8XXX g)',
              'EDTA appears as a white crystalline powder',
            ],
          }
        },
        {
          id: 2,
          title: 'Transfer to Beaker',
          shortDescription: 'Quantitatively transfer EDTA into 500 mL beaker',
          handler: 'handleTransferToFlask',
          validation: {
            requires: ['edtaMass']
          }
        },
        {
          id: 3,
          title: 'Add NaOH Pellet and Water',
          shortDescription: 'Add one pellet of NaOH and about 400 mL DI water',
          handler: 'handleAddWater',
          infoContent: {
            title: 'Dissolution',
            content: 'Place **one pellet of NaOH** and add about **400 mL of DI water** (keep in mind you will need to quantitatively transfer this solution to a volumetric flask eventually, which will add additional volume). Dissolve the EDTA by stirring with a glass stir rod or magnetic stir bar. Gentle heating up to **40 °C** is permitted but cool the solution before transferring to a volumetric flask.',
            whatHappens: 'A NaOH pellet is added to help dissolve the EDTA salt, and approximately 400 mL of DI water is used to begin dissolution with stirring.',
            observe: [
              'The NaOH pellet increases pH, helping the EDTA dissolve faster',
              'Stir until no visible solid remains in the beaker',
              'Gentle heating up to 40 C is permitted but the solution must be cooled before transfer',
            ],
          }
        },
        {
          id: 4,
          title: 'Dissolve EDTA',
          shortDescription: 'Stir until no more solid EDTA remains; gentle heating up to 40 °C permitted',
          handler: 'handleDissolve',
          infoContent: {
            title: 'Complete Dissolution',
            content: 'Continue stirring until no more solid EDTA remains. Gentle heating up to 40 °C is permitted but cool the solution before transferring to the volumetric flask.',
            whatHappens: 'Continuous stirring (and optional gentle heating) ensures all EDTA solid dissolves completely into a clear, homogeneous solution.',
            observe: [
              'Monitor the solution for any remaining undissolved solid particles',
              'The solution should become completely clear when dissolution is complete',
              'If heating was used, allow the solution to cool to room temperature before proceeding',
            ],
          }
        },
        {
          id: 5,
          title: 'Transfer to Volumetric Flask',
          shortDescription: 'Quantitatively transfer to 500 mL volumetric flask and dilute to mark',
          handler: 'handleDiluteToMark',
          validation: {
            requires: ['isDissolving']
          },
          infoContent: {
            title: 'Quantitative Transfer',
            content: 'Once no more solid EDTA remains, transfer **quantitatively** to a volumetric flask and dilute to the mark with deionised water.',
            whatHappens: 'The dissolved EDTA is quantitatively transferred to a 500 mL volumetric flask and diluted to the mark for a precise final volume.',
            observe: [
              'Rinse the beaker and stir rod with DI water to ensure all EDTA is transferred',
              'Add deionised water until the bottom of the meniscus touches the 500 mL mark',
              'Ensure no solid residue remains in the original beaker',
            ],
          }
        },
        {
          id: 6,
          title: 'Mix Thoroughly',
          shortDescription: 'Invert flask several times to homogenize',
          handler: 'handleMixSolution'
        }
      ]
    },
    {
      id: 2,
      name: 'standardization',
      title: 'Part II: Standardization',
      equipment: ['Buret', 'Erlenmeyer flasks (3)', 'Pipette (25 mL)', 'pH 10 buffer', 'EBT indicator'],
      initialState: {
        currentTrial: 1,
        mgSolutionAdded: false,
        bufferAdded: false,
        indicatorAdded: false,
        flaskColor: '#FFFFFF',
        burette: {
          initialVolume: 0,
          currentVolume: 0,
          isOpen: false
        },
        titrationData: [
          { trial: 1, vi: null, vf: null, volumeEDTA: null },
          { trial: 2, vi: null, vf: null, volumeEDTA: null },
          { trial: 3, vi: null, vf: null, volumeEDTA: null }
        ]
      },
      steps: [
        {
          id: 1,
          title: 'Pipette Mg Standard',
          shortDescription: 'Transfer 25.00 mL of Mg2+ standard to flask',
          handler: 'handlePipetteMgStandard',
          infoContent: {
            title: 'Standard Solution (0.01 M Mg²⁺)',
            content: 'Pipette **3 × 25 mL aliquots** of **0.01 M Mg²⁺** standard solution (MgCl₂) into **3 separate Erlenmeyer flasks**. The magnesium standard has a precisely known concentration. By determining how much EDTA is needed to titrate this known amount, we can calculate the exact EDTA concentration.',
            whatHappens: 'A precisely known volume of Mg2+ standard is pipetted into three flasks to provide a known number of moles for standardizing the EDTA solution.',
            observe: [
              'Use a rubber safety bulb with the 25 mL pipette — never pipet by mouth',
              'Pipette exactly 25.00 mL into each of the three Erlenmeyer flasks',
              'The Mg2+ standard solution appears clear and colourless',
            ],
          }
        },
        {
          id: 2,
          title: 'Add pH 10 Buffer',
          shortDescription: 'Add 1 mL of pH 10 ammonia/ammonium chloride buffer',
          handler: 'handleAddBuffer',
          validation: {
            requires: ['mgSolutionAdded']
          },
          infoContent: {
            title: 'Why pH 10?',
            content: `Add **1 mL** of **pH 10 ammonia ammonium chloride buffer** to each flask.

At pH 10, EDTA reacts efficiently with Mg²⁺. The EBT indicator shows its best colour change at this pH.

**CAUTION:** Ammonia is a respiratory irritant and heart stimulant. Confine the pouring of the ammonia buffer to the exhaust hood.`,
            whatHappens: 'The ammonia/ammonium chloride buffer raises the pH to 10, which is required for EDTA to form stable complexes with Mg2+ and for the EBT indicator to function properly.',
            observe: [
              'Add the buffer in the exhaust hood due to ammonia fumes',
              'The solution should remain colourless after buffer addition',
              'Verify that 1 mL buffer is added to each flask',
            ],
          }
        },
        {
          id: 3,
          title: 'Add EBT Indicator',
          shortDescription: 'Add 2-4 drops of Eriochrome Black T (or small amount of calmagite powder)',
          handler: 'handleAddIndicator',
          validation: {
            requires: ['bufferAdded']
          },
          infoContent: {
            title: 'EBT Indicator',
            content: 'Add **2-4 drops** of **Eriochrome Black T** indicator solution (or a small amount of calmagite powder) to each flask. EBT forms a **red** complex with Mg²⁺ ions. When EDTA removes all the metal from the indicator, the colour changes to blue.',
            whatHappens: 'Eriochrome Black T indicator binds to free Mg2+ ions in solution, turning the solution red/wine-red; at the endpoint, EDTA strips the Mg2+ from the indicator, causing a colour change to blue.',
            observe: [
              'The solution turns red or wine-red after adding EBT indicator',
              'This red colour confirms Mg2+ ions are present and bound to the indicator',
              'Add 2-4 drops — too much indicator makes the endpoint harder to see',
            ],
          }
        },
        {
          id: 4,
          title: 'Fill Burette',
          shortDescription: 'Fill burette with EDTA solution to 0 mL mark',
          handler: 'handleFillBurette',
          validation: {
            requires: ['indicatorAdded']
          }
        },
        {
          id: 5,
          title: 'Record Initial Volume',
          shortDescription: 'Record the initial burette reading',
          handler: 'handleRecordInitialVolume'
        },
        {
          id: 6,
          title: 'Titrate to Endpoint',
          shortDescription: 'Add EDTA until colour changes from red to distinct blue',
          handler: 'handleTitrate',
          infoContent: {
            title: 'The Endpoint',
            content: `Titrate the Mg²⁺ standard solution samples with the EDTA solution to a colour change from **red to distinct blue**.

Continue adding EDTA dropwise until the blue colour persists. The last drop that turns the solution definitively blue is the endpoint.

A **blank correction** is also required: titrate 25 mL of deionised water to which 1 mL pH 10 buffer and 2-4 drops of EBT has been added. Subtract the blank EDTA volume from each Mg²⁺ titre.`,
            chemicalEquation: 'M(EDTA) = (Volume Mg²⁺ std (L) × Molarity Mg²⁺ std) / Titre EDTA (L)',
            whatHappens: 'EDTA is added from the burette until all Mg2+ is chelated; the endpoint is marked by a distinct colour change from red to blue as EDTA strips the last Mg2+ from the EBT indicator.',
            observe: [
              'The red colour gradually lightens as EDTA chelates free Mg2+ ions',
              'Near the endpoint, the solution transitions through purple to blue',
              'The endpoint is a distinct, persistent blue colour — not purple or grey',
              'Perform a blank titration (25 mL DI water + buffer + EBT) to determine the blank correction',
            ],
          }
        },
        {
          id: 7,
          title: 'Record Final Volume',
          shortDescription: 'Record the final burette reading',
          handler: 'handleRecordFinalVolume'
        },
        {
          id: 8,
          title: 'Repeat Titration',
          shortDescription: 'Perform trials 2 and 3 for reproducibility',
          handler: 'handleRepeatTitration'
        }
      ]
    },
    {
      id: 3,
      name: 'sampleAnalysis',
      title: 'Part III: Calcium in Water',
      equipment: ['Buret', '250 mL Erlenmeyer flasks (3)', '25 mL pipette', 'Rubber safety bulb'],
      initialState: {
        sampleAdded: false,
        bufferAdded: false,
        indicatorAdded: false,
        flaskColor: '#FFFFFF',
        sampleResult: {
          volumeEDTA: null,
          ppmCalcium: null
        }
      },
      steps: [
        {
          id: 1,
          title: 'Obtain and Dilute Unknown Sample',
          shortDescription: 'Obtain unknown water sample, dilute 5-fold with DI water, pipette 25 mL into each of 3 flasks',
          handler: 'handlePipetteSample',
          infoContent: {
            title: 'Water Sample Preparation',
            content: 'Obtain an unknown sample of water for calcium analysis. Record the source. **Dilute the sample 5-fold** using DI water. Wash three 250 mL Erlenmeyer flasks and rinse with deionised water. Label 1, 2, 3. Using a rubber safety bulb, **pipet 25 mL** of diluted water sample into each flask.',
            whatHappens: 'The unknown water sample is diluted 5-fold to bring the calcium concentration into the optimal titration range, then 25 mL aliquots are pipetted into three flasks for triplicate analysis.',
            observe: [
              'Record the source of the unknown water sample',
              'Dilute the sample 5-fold with DI water before pipetting',
              'Pipet exactly 25.00 mL of diluted sample into each of three labelled flasks',
            ],
          }
        },
        {
          id: 2,
          title: 'Add Buffer, Mg/EDTA, and Indicator',
          shortDescription: 'Add 2 mL buffer, 1 mL Mg/EDTA, and 5-6 drops EBT (at exhaust hood)',
          handler: 'handleAddBufferAndIndicator',
          validation: {
            requires: ['sampleAdded']
          },
          infoContent: {
            title: 'Adding Reagents to Water Sample',
            content: `Take the three flasks to the **exhaust hood** and add:
- **2 mL** of ammonia/ammonium chloride buffer (Caution!) to each flask
- **1 mL** of Mg/EDTA solution (promotes sharper colour change at endpoint)
- **5-6 drops** of Eriochrome Black T indicator solution

The sample should be **red** at this point. EDTA works best in basic solution — the buffer ensures pH ~10.`,
            whatHappens: 'Buffer, Mg/EDTA solution, and EBT indicator are added to each flask at the exhaust hood; the solution turns red, indicating Ca2+ and Mg2+ are binding to the indicator.',
            observe: [
              'Add reagents at the exhaust hood due to ammonia fumes from the buffer',
              'The Mg/EDTA solution provides a small amount of Mg2+ to sharpen the endpoint colour change',
              'The solution should turn distinctly red after adding EBT indicator',
            ],
          }
        },
        {
          id: 3,
          title: 'Refill Burette',
          shortDescription: 'Ensure burette is filled with EDTA',
          handler: 'handleRefillBurette',
          validation: {
            requires: ['indicatorAdded']
          }
        },
        {
          id: 4,
          title: 'Titrate Sample',
          shortDescription: 'Add EDTA until red/purple disappears and pure blue appears',
          handler: 'handleTitrateSample',
          infoContent: {
            title: 'Water Sample Titration',
            content: 'Begin titrating with standard EDTA from the buret. The **red** colour will gradually change to **purple/grey** as the blue calcium/EDTA complex forms. The endpoint is signalled by the disappearance of the red/purple colour and the appearance of the **pure blue** colour. Record initial and final buret volumes to the nearest **0.02 mL**. Repeat with samples 2 and 3.',
            whatHappens: 'EDTA is slowly added from the burette to chelate Ca2+ in the water sample; the colour transitions from red through purple to pure blue at the endpoint.',
            observe: [
              'The solution changes from red to purple/grey as free Ca2+ is consumed',
              'The endpoint is a sharp transition from purple to pure blue',
              'Record burette volumes to the nearest 0.02 mL for accurate results',
              'Repeat the titration for all three flasks for reproducibility',
            ],
          }
        },
        {
          id: 5,
          title: 'Record Volume',
          shortDescription: 'Record volume of EDTA used',
          handler: 'handleRecordSampleVolume'
        }
      ]
    },
    {
      id: 4,
      name: 'calculations',
      title: 'Part IV: Calculations',
      equipment: [],
      initialState: {
        calculatedMolarity: null,
        calculatedPPM: null,
        hardnessClassification: null
      },
      steps: [
        {
          id: 1,
          title: 'Calculate EDTA Molarity',
          shortDescription: 'Calculate EDTA concentration from standardization',
          handler: 'handleCalculateMolarity',
          infoContent: {
            title: 'EDTA Molarity Calculation',
            content: `EDTA reacts **1:1** with metal ions: moles EDTA = moles Mg²⁺.

**Formula:** Molarity (M) EDTA = (Volume Mg²⁺ std. (L) × Molarity Mg²⁺ std. (mol/L)) / Titre EDTA (L)

Remember to subtract the blank correction volume from each titre before calculating.

Average all three trials for the final EDTA molarity.`,
            chemicalEquation: 'M(EDTA) = (V_Mg × M_Mg) / V_EDTA',
            whatHappens: 'The EDTA molarity is calculated from the standardization data using the 1:1 stoichiometry between EDTA and Mg2+, after subtracting the blank correction volume.',
            observe: [
              'Subtract the blank titre volume from each Mg2+ titre before calculating',
              'Average the three trial results for a reliable EDTA molarity',
              'The calculated molarity should be close to the expected ~0.01 M',
            ],
          }
        },
        {
          id: 2,
          title: 'Calculate Calcium ppm',
          shortDescription: 'Determine calcium content in water sample',
          handler: 'handleCalculatePPM',
          validation: {
            requires: ['calculatedMolarity'],
          },
          infoContent: {
            title: 'ppm Calcium Calculation',
            content: `**Step 1:** moles EDTA = Titre EDTA (L) × Molarity EDTA
**Step 2:** moles EDTA = moles Ca²⁺ in sample (1:1 stoichiometry)
**Step 3:** ppm Ca²⁺ = (moles Ca²⁺ × Molar Mass Ca²⁺ × 10⁶) / 25 g

Where: ppm = (g solute / g solution) × 10⁶

Your sample size was **25.00 mL**. Assume the density of the sample is **1.0 g/mL**. Remember the sample was diluted **5-fold**, so account for this in the final result.`,
            whatHappens: 'Moles of Ca2+ are determined from the EDTA titre using 1:1 stoichiometry, then converted to ppm, accounting for the 5-fold dilution of the original sample.',
            observe: [
              'Calculate moles Ca2+ = moles EDTA = titre (L) x EDTA molarity',
              'Convert to ppm using: ppm = (moles x molar mass x 10^6) / sample mass (25 g)',
              'Multiply the result by 5 to correct for the 5-fold dilution',
            ],
          }
        },
        {
          id: 3,
          title: 'Classify Water Hardness',
          shortDescription: 'Compare result to hardness scale',
          handler: 'handleClassifyHardness',
          validation: {
            requires: ['calculatedPPM']
          },
          infoContent: {
            title: 'Questions',
            content: `**Lab Questions:**
1. What is the expected amount of Ca²⁺ in water from the Durham region? (Internet or library)
2. What was your result and how do your results compare with expected quantities of Ca²⁺ in water?
3. Calculate the expected Molarity of the EDTA solution prepared in Part I. Remember that disodium salt of EDTA was used.
4. Compare the Molarity determined from standardization with the value you calculated above (calculate % difference).
5. How would you make 100 mL of 0.03 M HCl from a 3 M HCl stock solution? Show all steps and calculations.`,
            whatHappens: 'The calcium ppm result is compared against expected regional water hardness values, and the experimental EDTA molarity is compared to the calculated theoretical value.',
            observe: [
              'Compare your Ca2+ ppm to expected Durham region water data',
              'Calculate percent difference between standardized and theoretical EDTA molarity',
              'Show complete dilution calculations for the HCl question (M1V1 = M2V2)',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp4',
    title: 'EDTA Standardization Quiz',
    questions: [
      {
        id: 'exp4-q1',
        type: 'mcq' as const,
        question: 'EDTA is classified as what type of ligand?',
        options: [
          { id: 'a', text: 'Monodentate' },
          { id: 'b', text: 'Bidentate' },
          { id: 'c', text: 'Tetradentate' },
          { id: 'd', text: 'Hexadentate' },
        ],
        correctOptionId: 'd',
        explanation: 'EDTA (ethylenediaminetetraacetic acid) has six potential bonding sites (4 carboxylate groups + 2 amine groups), making it a hexadentate ligand.',
      },
      {
        id: 'exp4-q2',
        type: 'mcq' as const,
        question: 'What is the endpoint color change when titrating with Eriochrome Black T (EBT) indicator?',
        options: [
          { id: 'a', text: 'Blue to red' },
          { id: 'b', text: 'Red to distinct blue' },
          { id: 'c', text: 'Yellow to green' },
          { id: 'd', text: 'Colorless to pink' },
        ],
        correctOptionId: 'b',
        explanation: 'EBT indicator changes from red (when bound to metal ions) to distinct blue (when free in solution) at the equivalence point.',
      },
      {
        id: 'exp4-q3',
        type: 'calculation' as const,
        question: 'If 25.00 mL of 0.01 M Mg\u00B2⁺ standard required 24.50 mL of EDTA to reach the endpoint, what is the molarity of the EDTA solution?',
        expectedValue: 0.01020,
        tolerance: 3,
        unit: 'M',
        explanation: 'M(EDTA) = (Vₘₗ × Mₘₗ) / V(EDTA) = (25.00 mL × 0.01 M) / 24.50 mL = 0.01020 M',
      },
      {
        id: 'exp4-q4',
        type: 'short_answer' as const,
        question: 'Why is a blank titration performed with 25 mL of deionised water, buffer, and EBT indicator?',
        sampleAnswer: 'The blank corrects for any impurities in the reagents (buffer, indicator, or water) that might react with EDTA. The blank volume is subtracted from the sample titration volumes to get accurate net titre values.',
      },
      {
        id: 'exp4-q5',
        type: 'mcq' as const,
        question: 'Why is ammonia/ammonium chloride buffer (pH 10) used in this titration?',
        options: [
          { id: 'a', text: 'To dissolve the EDTA' },
          { id: 'b', text: 'To maintain pH 10 where EDTA fully complexes with metal ions and EBT works properly' },
          { id: 'c', text: 'To precipitate interfering ions' },
          { id: 'd', text: 'To prevent oxidation of the sample' },
        ],
        correctOptionId: 'b',
        explanation: 'At pH 10, EDTA is fully deprotonated (Y⁴⁻ form) and reacts quantitatively with metal ions. EBT indicator also functions best at this pH.',
      },
    ],
  },
};

export default experiment4Config;
